import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { beginDefinitionSync, endDefinitionSync } from '@/tableDefinitionEditor/fetch/definitionSyncGate';
import { addOrUpdateColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { GetTableDefinitionQueryKey, sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import type { QueryClient } from '@tanstack/react-query';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';

/** Cached indefinitely: every change publishes its own result, so a staleness timer has nothing to catch. */
const evidenceTableDefinitionQueryOptions = (tableDefinitionId: string) =>
  queryOptions({
    queryKey: GetTableDefinitionQueryKey({ id: tableDefinitionId }),
    queryFn: () => sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId }).then(({ data }) => data),
    staleTime: Infinity,
  });

export const useEvidenceTableDefinitionQuery = (tableDefinitionId: string | undefined) =>
  useQuery({ ...evidenceTableDefinitionQueryOptions(tableDefinitionId!), enabled: !!tableDefinitionId });

/** The cached definition, for fetches that run outside React and cannot wait for a render. */
export const getEvidenceTableDefinition = (queryClient: QueryClient, tableDefinitionId: string) =>
  queryClient.getQueryData(evidenceTableDefinitionQueryOptions(tableDefinitionId).queryKey);

/** Publishes a definition the caller already has, so readers update without a round trip. */
export const cacheEvidenceTableDefinition = (queryClient: QueryClient, definition: TableDefinitionOutputV1): void => {
  queryClient.setQueryData(evidenceTableDefinitionQueryOptions(definition.id).queryKey, definition);
};

/** Re-reads the definition from the backend and publishes it, whether or not anything is rendering it. */
export const refetchEvidenceTableDefinition = (
  queryClient: QueryClient,
  tableDefinitionId: string,
): Promise<TableDefinitionOutputV1> =>
  queryClient.fetchQuery({ ...evidenceTableDefinitionQueryOptions(tableDefinitionId), staleTime: 0 });

/**
 * Adds or updates one evidence table column and publishes the resulting definition.
 * Pass the id of the column being edited, or nothing to add a new one.
 */
export const useSaveEvidenceColumn = (tableDefinitionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      columnDefinition,
      columnId,
    }: {
      columnDefinition: ColumnDefinitionInputV1;
      columnId?: string;
    }) => {
      const output = await addOrUpdateColumnDefinition(tableDefinitionId, columnDefinition, columnId);
      // A missing output means the save failed; addOrUpdateColumnDefinition has already reported it.
      if (!output) return undefined;

      cacheEvidenceTableDefinition(queryClient, output.tableDefinition);
      return output.tableDefinition.columnDefinitions;
    },
    // The server broadcasts row events mid-save but omits this session from `columnUpdateInProgress`, so nothing
    // else tells us to stop fetching rows against the pre-save columns until the response lands.
    onMutate: () => beginDefinitionSync(tableDefinitionId),
    onSettled: () => endDefinitionSync(tableDefinitionId),
  });
};
